import { useState } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import useApiFetch from '@/hooks/useApiFetch';
import useApiFetches from '@/hooks/useApiFetches';
import ptosServices from '@/services/ptos';
import staffMemberServices from '@/services/staffMember';
import { chakraExtendedTheme } from '@/styles/theme';
import { FetchApiIds } from '@/types';
import { PTOStatus } from '@/types/api';
import { StaffMember } from '@/types/api/staffMember';
import PTORequestsComponent, { Filter } from './PTORequests.component';
import Context from './PTORequests.context';

const ptoStatusByFilter: { [filter in Filter]: PTOStatus | undefined } = {
  All: undefined,
  Unanswered: PTOStatus.PENDING,
  Schedule: PTOStatus.APPROVED,
};

const PTORequestsPage = function (): JSX.Element {
  const [filter, setFilter] = useState<Filter>('Unanswered');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
  });

  const {
    data: getPTOsResponse,
    isLoading: isLoadingPtos,
    refetch: refetchPTOs,
  } = useApiFetch(
    [FetchApiIds.GET_PTOS_AS_ADMIN, pagination.currentPage, pagination.itemsPerPage, filter],
    async () =>
      ptosServices.getPTOsAsAdmin({
        page: pagination.currentPage,
        take: filter === 'Schedule' ? 9999 : pagination.itemsPerPage,
        filter: ptoStatusByFilter[filter],
        activeFromToday: filter === 'Schedule',
        order: 'ASC',
      })
  );

  const onChangePageHandler = (currentPage: number): void => {
    setPagination((prev) => ({
      ...prev,
      currentPage,
    }));
  };

  const onChangeRecordsPerPage = (itemsPerPage: number): void => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
      itemsPerPage,
    }));
  };

  const onChangeFilter = (newFilter: Filter): void => {
    if (newFilter !== filter) {
      setPagination((prev) => ({
        ...prev,
        currentPage: 1,
      }));
      setFilter(newFilter);
    }
  };

  const staffMembers = new Set<string>(
    (getPTOsResponse?.data || []).map((pto) => pto.staffMemberId)
  );

  const getStaffMemberResults = useApiFetches<StaffMember>(
    [...staffMembers].map((staffMemberId) => ({
      queryKey: [FetchApiIds.GET_STAFF_MEMBER, staffMemberId],
      fn: async () => staffMemberServices.getStaffMember({ staffMemberId }),
    }))
  );

  const fetchStaffMembers = [...staffMembers].reduce<Record<string, any>>((obj, curr, i) => {
    return {
      ...obj,
      [curr]: getStaffMemberResults[i],
    };
  }, {});

  return (
    <ChakraProvider theme={chakraExtendedTheme}>
      <Context.Provider value={{ fetchStaffMembers }}>
        <PTORequestsComponent
          onChangePageHandler={onChangePageHandler}
          onChangeRecordsPerPage={onChangeRecordsPerPage}
          ptoRequestList={getPTOsResponse?.data}
          count={getPTOsResponse?.count || 0}
          currentPage={pagination.currentPage}
          itemsPerPage={pagination.itemsPerPage}
          lastPage={getPTOsResponse?.lastPage || 1}
          filter={filter}
          onChangeFilter={onChangeFilter}
          isLoadingPTOs={isLoadingPtos}
          onUpdatePTO={refetchPTOs}
        />
      </Context.Provider>
    </ChakraProvider>
  );
};

export default PTORequestsPage;
